/**
 * Modal Détail Demande — Aides Complémentaires
 * Résumé · Critères · Coordonnées · Sources · Brouillon · Pièces · Actions
 */
import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { toast } from "sonner";
import {
  X, Globe, Mail, MapPin, Phone, FileText, CheckCircle2,
  AlertCircle, ExternalLink, Edit3, Send, Printer, Save,
  Clock, XCircle, Loader2, Info, ChevronDown, ChevronUp
} from "lucide-react";
import { API, getAuthHeaders, formatDate } from "./helpers";
import { VerdictBadge, MontantCode, RegleEtManquants, EtiquetteBrouillon, verdictDuCode } from "./VerdictAide";

// ─── Config statuts ──────────────────────────────────────────────────────────

const STATUT_CFG = {
  non_etudiee:      { label: "Non étudiée",       color: "var(--oh-stat-slate-text)", icon: Clock },
  infos_manquantes: { label: "Infos manquantes",  color: "var(--oh-stat-amber-text)", icon: AlertCircle },
  brouillon_genere: { label: "Brouillon généré",  color: "var(--oh-stat-blue-text)", icon: FileText },
  a_valider:        { label: "À valider",          color: "#8b5cf6", icon: CheckCircle2 },
  validee:          { label: "Validée",            color: "#10b981", icon: CheckCircle2 },
  envoyee:          { label: "Envoyée",            color: "#059669", icon: Send },
  refusee:          { label: "Refusée",            color: "var(--oh-stat-red-text)", icon: XCircle },
  erreur:           { label: "Erreur",             color: "var(--oh-stat-red-text)", icon: AlertCircle },
};

const CANAL_CFG = {
  email:          { label: "Email",           icon: Mail },
  courrier:       { label: "Courrier postal", icon: Printer },
  formulaire_web: { label: "Formulaire web",  icon: Globe },
};

// ─── Composant principal ──────────────────────────────────────────────────────

export function DemandeDetailModal({ demande: initialDemande, dossierId, tenantId, onClose, onRefresh }) {
  const [demande, setDemande] = useState(initialDemande);
  const [editingContent, setEditingContent] = useState(false);
  const [editedSujet, setEditedSujet] = useState(initialDemande.sujet || "");
  const [editedContenu, setEditedContenu] = useState(initialDemande.contenu || "");
  const [editingCoords, setEditingCoords] = useState(false);
  const [coords, setCoords] = useState({
    contact_nom: initialDemande.contact_nom || "",
    contact_email: initialDemande.contact_email || "",
    contact_adresse: initialDemande.contact_adresse || "",
    contact_telephone: initialDemande.contact_telephone || "",
  });
  const [infosValues, setInfosValues] = useState(
    Object.fromEntries((initialDemande.infos_requises || []).map(i => [i.cle, i.valeur || ""]))
  );
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [validating, setValidating] = useState(false);
  const [showSources, setShowSources] = useState(false);

  const headers = getAuthHeaders(tenantId);
  const statutCfg = STATUT_CFG[demande.statut] || STATUT_CFG.non_etudiee;
  const StatutIcon = statutCfg.icon;
  const canalCfg = CANAL_CFG[demande.canal] || CANAL_CFG.courrier;
  const CanalIcon = canalCfg.icon;

  const infosRequises = demande.infos_requises || [];
  const infosManquantes = infosRequises.filter(i => i.requis && !infosValues[i.cle]);

  async function sauvegarderModifications(extra = {}) {
    setSaving(true);
    try {
      const updatedInfos = infosRequises.map(i => ({
        ...i,
        valeur: infosValues[i.cle] || i.valeur,
      }));
      const payload = {
        sujet: editedSujet,
        contenu: editedContenu,
        infos_requises: updatedInfos,
        ...coords,
        ...extra,
      };
      const res = await fetch(
        `${API}/api/aides/dossier/${dossierId}/demandes/${demande.id}`,
        {
          method: "PUT",
          headers: { ...headers, "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );
      if (res.ok) {
        const updated = await res.json();
        setDemande(updated);
        setEditingContent(false);
        setEditingCoords(false);
        toast.success("Modifications sauvegardées");
        onRefresh?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur sauvegarde");
      }
    } finally {
      setSaving(false);
    }
  }

  async function valider() {
    setValidating(true);
    try {
      // Sauvegarder d'abord les infos remplies
      await sauvegarderModifications();
      const res = await fetch(
        `${API}/api/aides/dossier/${dossierId}/demandes/${demande.id}/valider`,
        { method: "POST", headers, credentials: "include" }
      );
      if (res.ok) {
        const r = await res.json();
        setDemande(d => ({ ...d, statut: r.nouveau_statut }));
        toast.success("Demande validée — prête à envoyer");
        onRefresh?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur validation");
      }
    } finally {
      setValidating(false);
    }
  }

  async function envoyer() {
    setSending(true);
    try {
      const res = await fetch(
        `${API}/api/aides/dossier/${dossierId}/demandes/${demande.id}/envoyer`,
        { method: "POST", headers, credentials: "include" }
      );
      if (res.ok) {
        const r = await res.json();
        setDemande(d => ({ ...d, statut: "envoyee" }));
        toast.success(r.canal === "email" ? `Email envoyé à ${r.destinataire}` : r.message);
        onRefresh?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur envoi");
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
      data-testid="demande-detail-modal"
    >
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl"
        style={{ background: "var(--oh-bg)", border: "1px solid var(--oh-border)" }}
      >
        {/* ── Header ── */}
        <div
          className="sticky top-0 z-10 flex items-start justify-between gap-3 p-4"
          style={{ background: "var(--oh-bg)", borderBottom: "1px solid var(--oh-border)" }}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ background: statutCfg.color + "15", color: statutCfg.color }}
              >
                <StatutIcon className="w-3 h-3" />
                {statutCfg.label}
              </span>
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
                style={{ background: "var(--oh-bg-elevated)", color: "var(--oh-text-muted)", border: "1px solid var(--oh-border)" }}
              >
                <CanalIcon className="w-3 h-3" />
                {canalCfg.label}
              </span>
              <VerdictBadge entree={demande} testId="verdict-demande-modal" />
            </div>
            <h2 className="text-base font-medium mt-1.5" style={{ color: "var(--oh-text)" }}>
              {demande.aide_nom}
            </h2>
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
              {demande.aide_organisme}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:opacity-70 flex-shrink-0"
            style={{ color: "var(--oh-text-muted)" }} data-testid="close-demande-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">

          {/* ── 1. Verdict du code (T-085) — jamais un score, jamais un montant du modèle ── */}
          <Section title="Verdict d'éligibilité" icon={CheckCircle2}>
            <div className="space-y-2">
              {verdictDuCode(demande) ? (
                <>
                  <RegleEtManquants entree={demande} />
                  <p className="text-xs" style={{ color: "var(--oh-text-secondary)" }}>
                    Montant :{" "}
                    <MontantCode montant={demande.montant_calcule} formatMontant={(v) => `${Number(v).toLocaleString("fr-FR")} €`} />
                  </p>
                </>
              ) : (
                <p className="text-xs" style={{ color: "var(--oh-text-muted)" }} data-testid="ancienne-analyse">
                  Cette demande vient d'une ancienne analyse : le verdict n'a pas été établi par le code
                  et le score d'alors n'est plus affiché. Relancez l'analyse pour obtenir un verdict.
                </p>
              )}
            </div>
          </Section>

          {/* ── 2. Infos manquantes ── */}
          {infosRequises.length > 0 && (
            <Section
              title={`Informations requises ${infosManquantes.length > 0 ? `(${infosManquantes.length} manquante${infosManquantes.length > 1 ? "s" : ""})` : "✓"}`}
              icon={AlertCircle}
              accent={infosManquantes.length > 0 ? "var(--oh-stat-amber-text)" : "var(--oh-stat-green-text)"}
            >
              <div className="space-y-3">
                {infosRequises.map((info) => (
                  <div key={info.cle}>
                    <label className="text-xs font-medium" style={{ color: "var(--oh-text)" }}>
                      {info.label}
                      {info.requis && <span className="text-[var(--oh-error-text)] ml-1">*</span>}
                    </label>
                    {info.description && (
                      <p className="text-xs mt-0.5 mb-1" style={{ color: "var(--oh-text-muted)" }}>
                        {info.description}
                      </p>
                    )}
                    <input
                      className="w-full rounded-md px-3 py-1.5 text-xs"
                      style={{
                        background: "var(--oh-bg-elevated)",
                        border: `1px solid ${!infosValues[info.cle] && info.requis ? "var(--oh-stat-amber-text)" : "var(--oh-border)"}`,
                        color: "var(--oh-text)",
                      }}
                      placeholder={`Saisir ${info.label.toLowerCase()}…`}
                      value={infosValues[info.cle] || ""}
                      onChange={(e) => setInfosValues(v => ({ ...v, [info.cle]: e.target.value }))}
                      data-testid={`info-${info.cle}`}
                    />
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── 3. Coordonnées organisme ── */}
          <Section title="Coordonnées de l'organisme" icon={MapPin}
            action={
              <button onClick={() => setEditingCoords(!editingCoords)}
                className="text-xs flex items-center gap-1" style={{ color: "var(--oh-hub-aides)" }}>
                <Edit3 className="w-3 h-3" />
                {editingCoords ? "Annuler" : "Modifier"}
              </button>
            }
          >
            {editingCoords ? (
              <div className="space-y-2">
                {[
                  { key: "contact_nom", label: "Nom du service" },
                  { key: "contact_email", label: "Email" },
                  { key: "contact_adresse", label: "Adresse postale" },
                  { key: "contact_telephone", label: "Téléphone" },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{label}</label>
                    <input
                      className="w-full rounded-md px-3 py-1.5 text-xs mt-0.5"
                      style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
                      value={coords[key]}
                      onChange={(e) => setCoords(c => ({ ...c, [key]: e.target.value }))}
                      data-testid={`coord-${key}`}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-1.5">
                {coords.contact_nom && <InfoRow icon={MapPin} label="Service" value={coords.contact_nom} />}
                {coords.contact_email && <InfoRow icon={Mail} label="Email" value={coords.contact_email} isLink={`mailto:${coords.contact_email}`} />}
                {coords.contact_adresse && <InfoRow icon={MapPin} label="Adresse" value={coords.contact_adresse} />}
                {coords.contact_telephone && <InfoRow icon={Phone} label="Tél." value={coords.contact_telephone} />}
                {demande.contact_site && (
                  <a href={demande.contact_site} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs" style={{ color: "var(--oh-hub-aides)" }}>
                    <Globe className="w-3 h-3" />
                    Voir le site officiel
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {!coords.contact_nom && !coords.contact_email && !coords.contact_adresse && (
                  <p className="text-xs italic" style={{ color: "var(--oh-text-muted)" }}>
                    Aucune coordonnée trouvée — cliquez "Modifier" pour les saisir manuellement.
                  </p>
                )}
              </div>
            )}
            {demande.procedure_description && (
              <p className="text-xs mt-2 p-2 rounded-md"
                style={{ background: "var(--oh-bg-elevated)", color: "var(--oh-text-muted)" }}>
                <span className="font-medium" style={{ color: "var(--oh-text)" }}>Procédure :</span> {demande.procedure_description}
              </p>
            )}
          </Section>

          {/* ── 4. Sources web ── */}
          {demande.sources?.length > 0 && (
            <Section title={`Sources web (${demande.sources.length})`} icon={Globe}
              collapsible defaultCollapsed>
              <div className="space-y-2">
                {demande.sources.map((s, i) => (
                  <div key={i} className="text-xs">
                    <a href={s.url} target="_blank" rel="noopener noreferrer"
                      className="font-medium flex items-center gap-1 hover:underline"
                      style={{ color: "var(--oh-hub-aides)" }}>
                      {s.titre || s.url}
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    </a>
                    {s.extrait && (
                      <p className="mt-0.5" style={{ color: "var(--oh-text-muted)" }}>
                        {s.extrait.substring(0, 150)}…
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* ── 5. Brouillon ── */}
          <Section title="Brouillon de la demande" icon={FileText}
            action={
              !["envoyee", "refusee"].includes(demande.statut) && (
                <button onClick={() => setEditingContent(!editingContent)}
                  className="text-xs flex items-center gap-1" style={{ color: "var(--oh-hub-aides)" }}>
                  <Edit3 className="w-3 h-3" />
                  {editingContent ? "Annuler" : "Modifier"}
                </button>
              )
            }
          >
            {editingContent ? (
              <div className="space-y-2">
                <div>
                  <label className="text-xs font-medium" style={{ color: "var(--oh-text-muted)" }}>
                    Objet
                  </label>
                  <input
                    className="w-full rounded-md px-3 py-2 text-xs mt-1"
                    style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}
                    value={editedSujet}
                    onChange={(e) => setEditedSujet(e.target.value)}
                    data-testid="edit-sujet"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium" style={{ color: "var(--oh-text-muted)" }}>
                    Corps du message
                  </label>
                  <textarea
                    className="w-full rounded-md px-3 py-2 text-xs mt-1 font-mono resize-y"
                    style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)", color: "var(--oh-text)", minHeight: "200px" }}
                    value={editedContenu}
                    onChange={(e) => setEditedContenu(e.target.value)}
                    data-testid="edit-contenu"
                  />
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-2"><EtiquetteBrouillon demande={demande} /></div>
                <p className="text-xs font-medium mb-1" style={{ color: "var(--oh-text-muted)" }}>
                  Objet : <span style={{ color: "var(--oh-text)" }}>{demande.sujet}</span>
                </p>
                <pre
                  className="text-xs whitespace-pre-wrap font-sans p-3 rounded-md"
                  style={{ background: "var(--oh-bg-elevated)", color: "var(--oh-text)", border: "1px solid var(--oh-border)" }}
                  data-testid="contenu-brouillon"
                >
                  {demande.contenu}
                </pre>
              </div>
            )}
          </Section>

          {/* ── 6. Pièces jointes requises ── */}
          {demande.pieces_jointes_requises?.length > 0 && (
            <Section title={demande.pieces_suggerees ? "Pièces à joindre — suggestion, à vérifier" : "Pièces à joindre"} icon={FileText}>
              <ul className="space-y-1">
                {demande.pieces_jointes_requises.map((p, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs"
                    style={{ color: "var(--oh-text)" }}>
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ background: "var(--oh-hub-aides)" }} />
                    {p}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* ── Notes agent ── */}
          {demande.agent_notes && (
            <div className="text-xs p-3 rounded-md"
              style={{ background: "var(--oh-bg-elevated)", border: "1px dashed var(--oh-border)", color: "var(--oh-text-muted)" }}>
              <span className="font-medium">Note agent IA : </span>
              {demande.agent_notes}
            </div>
          )}
        </div>

        {/* ── Footer actions ── */}
        <div
          className="sticky bottom-0 flex items-center justify-between gap-3 p-4"
          style={{ background: "var(--oh-bg)", borderTop: "1px solid var(--oh-border)" }}
        >
          <div className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
            {demande.date_creation && `Créée le ${formatDate(demande.date_creation)}`}
            {demande.date_envoi && ` · Envoyée le ${formatDate(demande.date_envoi)}`}
          </div>

          <div className="flex items-center gap-2">
            {editingContent || editingCoords || Object.keys(infosValues).some(k => infosValues[k]) ? (
              <Button size="sm" variant="outline" onClick={() => sauvegarderModifications()} disabled={saving}
                className="text-xs h-8" data-testid="btn-sauvegarder">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                Sauvegarder
              </Button>
            ) : null}

            {!["envoyee", "refusee", "validee"].includes(demande.statut) && (
              <Button size="sm" variant="outline"
                onClick={valider} disabled={validating || infosManquantes.length > 0}
                className="text-xs h-8"
                title={infosManquantes.length > 0 ? "Complétez les informations requises d'abord" : ""}
                data-testid="btn-valider">
                {validating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                Valider
              </Button>
            )}

            {["validee", "a_valider"].includes(demande.statut) && (
              <Button size="sm" onClick={envoyer} disabled={sending}
                className="text-xs h-8"
                style={{ background: demande.canal === "email" ? "var(--oh-hub-aides)" : "#6b7280", color: "#fff" }}
                data-testid="btn-envoyer">
                {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CanalIcon className="w-3 h-3 mr-1" />}
                {sending ? "Envoi…" : demande.canal === "email" ? "Envoyer l'email" : "Marquer comme envoyé"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Section({ title, icon: Icon, action, children, collapsible, defaultCollapsed, accent }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed || false);
  return (
    <div className="rounded-lg overflow-hidden"
      style={{ border: `1px solid ${accent ? accent + "30" : "var(--oh-border)"}`, background: "var(--oh-bg-elevated)" }}>
      <div
        className="flex items-center justify-between px-3 py-2.5"
        style={{ borderBottom: collapsed ? "none" : "1px solid var(--oh-border)" }}
      >
        <div className="flex items-center gap-2">
          {collapsible ? (
            <button onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-2 text-xs font-medium"
              style={{ color: accent || "var(--oh-text)" }}>
              <Icon className="w-3.5 h-3.5" />
              {title}
              {collapsed ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
            </button>
          ) : (
            <span className="flex items-center gap-2 text-xs font-medium" style={{ color: accent || "var(--oh-text)" }}>
              <Icon className="w-3.5 h-3.5" />
              {title}
            </span>
          )}
        </div>
        {action && <div>{action}</div>}
      </div>
      {!collapsed && <div className="p-3">{children}</div>}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, isLink }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--oh-text-muted)" }} />
      <span style={{ color: "var(--oh-text-muted)" }}>{label} : </span>
      {isLink
        ? <a href={isLink} className="hover:underline" style={{ color: "var(--oh-hub-aides)" }}>{value}</a>
        : <span style={{ color: "var(--oh-text)" }}>{value}</span>
      }
    </div>
  );
}
